import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import ProductCard from "./ProductCard";

/**
 * ProductRail — THE product carousel, shared by the homepage
 * (Bestsellers, New Arrivals) and the department page so tiles,
 * arrows, view-all and swipe affordances stay identical everywhere
 * (owner: "same rail everywhere — view more, left/right scroll,
 * tile size").
 *
 * Mobile thumb-swipes (2 full cards + a 40px peek of the 3rd, plus
 * a right-edge fade as the "there's more" cue); desktop pages with
 * chevron arrows. View-all is a wine-outline secondary CTA in the
 * header (≥sm) and a full-width pill under the rail (mobile).
 */
export default function ProductRail({ id, sectionTestId, gridTestId, ctrlPrefix, className, eyebrow, title, subline, viewAllTo, viewAllLabel, items }) {
    const railRef = useRef(null);
    const [canPrev, setCanPrev] = useState(false);
    const [canNext, setCanNext] = useState(false);

    // Arrow enable/disable tracks real scroll position, so the affordance
    // is honest — no dead clicks at either end of the rail.
    const syncArrows = () => {
        const el = railRef.current;
        if (!el) return;
        // "End" tolerance = half a card: max scroll is rarely card-aligned,
        // and snap can rest the rail up to a card short of the hard edge —
        // without this, Next stays enabled for a dead 40px click.
        const half = el.firstElementChild
            ? el.firstElementChild.getBoundingClientRect().width / 2
            : 8;
        setCanPrev(el.scrollLeft > 8);
        setCanNext(el.scrollLeft < el.scrollWidth - el.clientWidth - Math.max(8, half));
    };
    useEffect(() => {
        const el = railRef.current;
        if (!el) return;
        syncArrows();
        el.addEventListener("scroll", syncArrows, { passive: true });
        window.addEventListener("resize", syncArrows);
        return () => {
            el.removeEventListener("scroll", syncArrows);
            window.removeEventListener("resize", syncArrows);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);

    // Hand-rolled glide instead of scrollBy({behavior:"smooth"}) — native
    // smooth scrolling on a snap-mandatory container is unreliable (Chromium
    // cancels it back to the current snap point; observed as dead arrows in
    // testing). Target is card-pitch aligned; snap is released for the
    // animation frames and restored after, so it can't fight the glide.
    const page = (dir) => {
        const el = railRef.current;
        if (!el || !el.firstElementChild) return;
        const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
        const pitch = el.firstElementChild.getBoundingClientRect().width + gap;
        const perPage = Math.max(1, Math.floor(el.clientWidth / pitch));
        const target = Math.max(0, Math.min(
            el.scrollWidth - el.clientWidth,
            Math.round((el.scrollLeft + dir * perPage * pitch) / pitch) * pitch
        ));
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            el.scrollLeft = target;
            syncArrows();
            return;
        }
        el.style.scrollSnapType = "none";
        const start = el.scrollLeft;
        const dist = target - start;
        const dur = 420;
        let t0 = null;
        let done = false;
        const finish = () => {
            done = true;
            el.scrollLeft = target;
            el.style.scrollSnapType = "";
            // Don't wait for the (paint-frame-deferred) scroll event —
            // sync the arrow enable/disable the moment the glide lands.
            syncArrows();
        };
        const ease = (t) => 1 - Math.pow(1 - t, 4); // ~ the brand's ease-out curve
        const step = (ts) => {
            if (done) return;
            if (t0 === null) t0 = ts;
            const p = Math.min(1, (ts - t0) / dur);
            el.scrollLeft = start + dist * ease(p);
            if (p < 1) requestAnimationFrame(step);
            else finish();
        };
        requestAnimationFrame(step);
        // rAF is paused in throttled/hidden contexts (battery saver, bg tab)
        // — if the glide hasn't started shortly, jump straight to the target
        // so the arrow never soft-fails.
        setTimeout(() => { if (t0 === null && !done) finish(); }, 150);
    };

    return (
        <section
            id={id}
            className={`${className} animate-fade-up scroll-mt-28`}
            data-testid={sectionTestId}
        >
            <div className="flex items-end justify-between gap-6 mb-5 sm:mb-8">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.32em] text-rosegold font-bold mb-2 sm:mb-3 flex items-center gap-3">
                        <span className="inline-block w-8 h-px bg-rosegold flex-shrink-0" />
                        {eyebrow}
                    </p>
                    <h2 className="font-display text-[1.8rem] sm:text-[2.6rem] lg:text-[3rem] leading-[1.05] text-ink tracking-tight font-light">
                        {title}
                    </h2>
                    {subline}
                </div>

                <div className="hidden sm:flex items-center gap-2.5 flex-shrink-0">
                    <Link
                        to={viewAllTo}
                        data-testid={`${ctrlPrefix}-view-all`}
                        className="inline-flex items-center gap-2 h-10 px-5 rounded-full
                                   border-[1.5px] border-wine/50 text-wine text-[10px] font-bold uppercase tracking-[0.22em]
                                   hover:bg-wine hover:text-ivory hover:border-wine transition-colors"
                    >
                        {viewAllLabel} <ArrowRight size={12} />
                    </Link>
                    {/* Chevrons: desktop-only (touch swipes); paired after
                        View-all so the row reads "see everything — or flip" */}
                    <span className="hidden lg:flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => page(-1)}
                            disabled={!canPrev}
                            aria-label="Previous pieces"
                            data-testid={`${ctrlPrefix}-prev`}
                            className="w-10 h-10 rounded-full border border-rosegold/50 bg-white text-ink flex items-center justify-center
                                       hover:border-wine hover:text-wine transition-colors
                                       disabled:opacity-30 disabled:pointer-events-none"
                        >
                            <ChevronLeft size={17} />
                        </button>
                        <button
                            type="button"
                            onClick={() => page(1)}
                            disabled={!canNext}
                            aria-label="More pieces"
                            data-testid={`${ctrlPrefix}-next`}
                            className="w-10 h-10 rounded-full border border-rosegold/50 bg-white text-ink flex items-center justify-center
                                       hover:border-wine hover:text-wine transition-colors
                                       disabled:opacity-30 disabled:pointer-events-none"
                        >
                            <ChevronRight size={17} />
                        </button>
                    </span>
                </div>
            </div>

            {/* Carousel on every breakpoint: 43% cards at 375 (2 full + 40px
                peek), fixed ~210px on desktop so a partial 6th card + the edge
                fade signal there's more behind the arrows. */}
            <div className="relative">
                <div
                    ref={railRef}
                    className="flex gap-3 lg:gap-6 overflow-x-auto pb-2 lg:pb-0 scrollbar-hide snap-x snap-mandatory"
                    data-testid={gridTestId}
                >
                    {items.map((i) => (
                        <div key={i.id} className="w-[43%] sm:w-[30%] lg:w-[210px] xl:w-[216px] flex-shrink-0 snap-start">
                            <ProductCard item={i} />
                        </div>
                    ))}
                </div>
                {/* Right-edge fade — on EVERY breakpoint while there is more
                    to scroll to. On mobile this is the main "it's draggable"
                    cue alongside the peeking card (owner: until I drag I
                    don't know it scrolls). */}
                {canNext && (
                    <div
                        aria-hidden
                        className="absolute inset-y-0 right-0 w-10 lg:w-20 pointer-events-none
                                   bg-gradient-to-l from-ivory to-transparent"
                    />
                )}
            </div>

            {/* Mobile view-all: full-width outlined pill — tappable, visible,
                still secondary to the page's Tier-1 CTA */}
            <Link
                to={viewAllTo}
                className="sm:hidden mt-4 flex w-full items-center justify-center gap-2 h-11 rounded-full
                           border-[1.5px] border-wine/50 text-wine text-[11px] font-bold uppercase tracking-[0.2em]
                           active:bg-wine/5 transition-colors"
                data-testid={`${ctrlPrefix}-view-all-mobile`}
            >
                {viewAllLabel} <ArrowRight size={12} />
            </Link>
        </section>
    );
}
